import { useEffect, useMemo, useState } from 'react';
import { Card, ConfirmModal, Field, Modal, Skeleton } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { closeMonthlyPeriod, fetchReportData, listenMonthlyPeriod, listenMonthlyPeriodEvents, markMonthlyReviewComplete, reopenMonthlyPeriod, saveBalanceAudit } from '../../firebase/repo';
import type { Account, BalanceAudit, MonthlyPeriod, MonthlyPeriodCloseSummary, MonthlyPeriodEvent } from '../../types';
import { displayDate, formatMoney, monthKey, todayISO } from '../../utils/date';
import { buildMonthlyCloseModel, resolveCloseMonthRange, type CreditCardReconciliationRow, type ReconciliationRow } from '../../utils/monthlyClose';
import { defaultMonthlyPeriod } from '../../utils/monthLocks';
import type { PageKey } from '../../app/AppShell';

type AuditDraft = {
  accountId: string;
  date: string;
  systemBalance: number;
  systemAvailableLimit: number;
  actualBalance: number;
  actualAvailableLimit: number;
  note: string;
};

type SourceData = Awaited<ReturnType<typeof fetchReportData>>;

const emptySource: SourceData = { transactions: [], payments: [], plans: [], crdr: [], budgets: [], accounts: [], accountLedger: [], balanceAudits: [], monthlyPeriods: [] };

const statusClass = (status: string) => status.toLowerCase().replace(/\s+/g, '-');

function Metric({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return <div className={`metric ${tone || ''}`}><span>{label}</span><b>{value}</b></div>;
}

function ReadinessDot({ state }: { state: 'ok' | 'warning' | 'critical' | 'info' }) {
  return <i className={`close-dot ${state}`}>{state === 'ok' ? 'OK' : state === 'critical' ? '!' : state === 'warning' ? '!' : 'i'}</i>;
}

export function MonthlyClosePage({ uid, onNavigate }: { uid: string; onNavigate: (page: PageKey) => void }) {
  const toast = useToast();
  const currentMonth = monthKey(todayISO());
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [source, setSource] = useState<SourceData>(emptySource);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState<AuditDraft | null>(null);
  const [period, setPeriod] = useState<MonthlyPeriod>(() => defaultMonthlyPeriod(uid, currentMonth));
  const [periodEvents, setPeriodEvents] = useState<MonthlyPeriodEvent[]>([]);
  const [periodAction, setPeriodAction] = useState<'review' | 'close' | 'reopen' | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setSource(await fetchReportData(uid));
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Monthly close data load failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [uid]);
  useEffect(() => {
    const unsubPeriod = listenMonthlyPeriod(uid, selectedMonth, setPeriod);
    const unsubEvents = listenMonthlyPeriodEvents(uid, selectedMonth, setPeriodEvents);
    return () => {
      unsubPeriod();
      unsubEvents();
    };
  }, [uid, selectedMonth]);

  const model = useMemo(() => buildMonthlyCloseModel(source, selectedMonth, todayISO()), [selectedMonth, source]);
  const range = resolveCloseMonthRange(selectedMonth);
  const closeSummary = useMemo((): MonthlyPeriodCloseSummary => ({
    warningCountAtClose: model.warnings.length,
    criticalCountAtClose: model.criticalIssues.length,
    reconciliationSummary: {
      liquidClosingBalance: model.closingLiquidBalance,
      cardOutstanding: model.closingCardOutstanding,
      reconciledAccounts: [...model.accountRows, ...model.cardRows].filter((row) => row.status === 'Matched').length,
      totalAccounts: model.accountRows.length + model.cardRows.length,
      unresolvedWarnings: model.warnings.length
    }
  }), [model]);

  const beginAudit = (row: ReconciliationRow | CreditCardReconciliationRow) => {
    const isCard = row.account.type === 'credit_card';
    const systemBalance = isCard && 'closingOutstanding' in row ? row.closingOutstanding : row.statement.closingBalance;
    const systemAvailableLimit = isCard ? Math.max(Number(row.account.creditLimit || 0) - Number(systemBalance || 0), 0) : 0;
    setAudit({
      accountId: row.account.id,
      date: range.toDate,
      systemBalance,
      systemAvailableLimit,
      actualBalance: row.actualBalance ?? systemBalance,
      actualAvailableLimit: isCard ? ('actualAvailableLimit' in row && row.actualAvailableLimit !== null ? row.actualAvailableLimit : systemAvailableLimit) : 0,
      note: `Monthly close review ${selectedMonth}`
    });
  };

  const auditAccount = source.accounts.find((account) => account.id === audit?.accountId);
  const auditDifference = audit ? Number(audit.actualBalance || 0) - Number(audit.systemBalance || 0) : 0;
  const availableDifference = audit && auditAccount?.type === 'credit_card' ? Number(audit.actualAvailableLimit || 0) - Number(audit.systemAvailableLimit || 0) : 0;

  const saveAudit = async () => {
    if (!audit || saving) return;
    setSaving(true);
    try {
      await saveBalanceAudit(uid, audit);
      toast('Reconciliation snapshot saved');
      setAudit(null);
      await load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Reconciliation save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const runPeriodAction = async () => {
    if (!periodAction || saving) return;
    setSaving(true);
    try {
      if (periodAction === 'review') {
        await markMonthlyReviewComplete(uid, selectedMonth, closeSummary);
        toast('Monthly review marked complete');
      }
      if (periodAction === 'close') {
        await closeMonthlyPeriod(uid, selectedMonth, closeSummary);
        toast('Month closed. Financial edits are now locked for this period.');
      }
      if (periodAction === 'reopen') {
        await reopenMonthlyPeriod(uid, selectedMonth, reopenReason);
        toast('Month reopened for corrections');
        setReopenReason('');
      }
      setPeriodAction(null);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Monthly close action failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const accountTable = (rows: ReconciliationRow[]) => rows.length ? <div className="table-wrap close-table"><table className="data-table"><thead><tr><th>Account</th><th>Opening</th><th>Credits</th><th>Debits</th><th>Closing</th><th>Actual</th><th>Difference</th><th>Status</th><th>Action</th></tr></thead><tbody>{rows.map((row) => <tr key={row.account.id}><td><b>{row.account.name}</b><small>{row.account.institutionName || row.account.type}</small></td><td>{formatMoney(row.statement.openingBalance)}</td><td className="amount-credit">{formatMoney(row.statement.totalCredit)}</td><td className="amount-debit">{formatMoney(row.statement.totalDebit)}</td><td>{formatMoney(row.statement.closingBalance)}</td><td>{row.actualBalance === null ? '-' : formatMoney(row.actualBalance)}</td><td className={row.difference && row.difference !== 0 ? 'amount-debit' : 'amount-credit'}>{row.difference === null ? '-' : formatMoney(row.difference)}</td><td><span className={`close-status ${statusClass(row.status)}`}>{row.status}</span></td><td><button className="btn small" onClick={() => beginAudit(row)}>Enter Actual</button></td></tr>)}</tbody></table></div> : <p className="compact-empty">No liquid accounts are available for reconciliation.</p>;

  return <div className="page monthly-close-page">
    <Card className="monthly-close-hero">
      <div className="dashboard-hero-head">
        <div>
          <p className="eyebrow">Month-end control</p>
          <h1>Monthly Close</h1>
          <p className="muted">Review account balances, commitments, and data quality before completing the month.</p>
        </div>
        <div className="close-header-actions">
          <Field label="Month"><input type="month" value={selectedMonth} max={currentMonth} onChange={(event) => setSelectedMonth(event.target.value > currentMonth ? currentMonth : event.target.value)} /></Field>
          <button className="btn" onClick={load} disabled={loading}>Refresh</button>
        </div>
      </div>
      <div className="close-status-strip">
        <div><span>Period lock</span><b className={`close-status ${statusClass(period.status)}`}>{period.status}</b></div>
        <div><span>Readiness</span><b className={`close-status ${statusClass(model.status)}`}>{model.status}</b></div>
        <div><span>Review range</span><b>{displayDate(model.fromDate)} to {displayDate(model.toDate)}</b></div>
        <div><span>Critical</span><b>{model.criticalIssues.length}</b></div>
        <div><span>Warnings</span><b>{model.warnings.length}</b></div>
      </div>
      {period.status === 'CLOSED'
        ? <p className="warning">This month is closed. Dated financial edits, deletes, transfers, card payments, plan paid records, and CR/DR changes are blocked until reopened.</p>
        : <p className="warning">Closing the month locks ordinary financial mutations by effective date. Reconciliation snapshots remain append-only and allowed.</p>}
    </Card>

    {loading ? <Card><Skeleton /></Card> : <>
      <Card>
        <div className="section-title"><div><h3>Month summary</h3><p className="muted">Transfers and card payments are shown separately and not double counted as income or expense.</p></div><button className="btn small" onClick={() => onNavigate('reports')}>Open Reports</button></div>
        <div className="metric-strip">
          <Metric label="Opening liquid balance" value={formatMoney(model.openingLiquidBalance)} />
          <Metric label="Closing liquid balance" value={formatMoney(model.closingLiquidBalance)} tone={model.closingLiquidBalance < 0 ? 'debit' : 'credit'} />
          <Metric label="Income" value={formatMoney(model.income)} tone="credit" />
          <Metric label="Expense" value={formatMoney(model.expense)} tone="debit" />
          <Metric label="Investment" value={formatMoney(model.investment)} tone="blue" />
          <Metric label="Net cash flow" value={formatMoney(model.netCashFlow)} tone={model.netCashFlow < 0 ? 'orange' : 'credit'} />
          <Metric label="Transfers" value={formatMoney(model.transferTotal)} />
          <Metric label="Card payments" value={formatMoney(model.cardPaymentTotal)} />
          <Metric label="Card outstanding" value={formatMoney(model.closingCardOutstanding)} tone="orange" />
        </div>
      </Card>

      <Card>
        <div className="section-title"><div><h3>Close readiness</h3><p className="muted">Automatically derived from reconciliations, linked ledgers, plans, budgets and data quality.</p></div></div>
        <div className="close-checklist">{model.checklist.map((item) => <div className={`close-check ${item.state}`} key={item.id}><ReadinessDot state={item.state} /><span><b>{item.label}</b><small>{item.detail}</small></span></div>)}</div>
      </Card>

      <Card>
        <div className="section-title"><div><h3>Account reconciliation</h3><p className="muted">Actual balance is saved as an audit snapshot only.</p></div></div>
        {accountTable(model.accountRows)}
      </Card>

      <Card>
        <div className="section-title"><div><h3>Credit card reconciliation</h3><p className="muted">Outstanding liability is reviewed separately from liquid cash.</p></div></div>
        <div className="account-card-grid">{model.cardRows.map((row) => <div className={`dashboard-card-card ${row.status === 'Mismatch' ? 'over_limit' : row.status === 'Matched' ? 'normal' : 'elevated'}`} key={row.account.id}>
          <div><b>{row.account.name}</b><span>{row.account.institutionName || 'Credit card'}{row.account.last4 ? ` / ****${row.account.last4}` : ''}</span></div>
          <strong>{formatMoney(row.closingOutstanding)}</strong>
          <small>Closing outstanding / actual {row.actualBalance === null ? '-' : formatMoney(row.actualBalance)}</small>
          <div className="budget-meter"><i style={{ width: `${Math.min(row.utilizationPct, 140)}%` }} /></div>
          <div className="account-movement"><span>Opening {formatMoney(row.openingOutstanding)}</span><span>Purchases {formatMoney(row.purchases)}</span><span>Payments {formatMoney(row.payments)}</span><span>Available {formatMoney(row.availableCredit)}</span></div>
          <span className={`close-status ${statusClass(row.status)}`}>{row.status}</span>
          <button className="btn small" onClick={() => beginAudit(row)}>Enter Actual Outstanding</button>
        </div>)}</div>
        {!model.cardRows.length && <p className="compact-empty">No credit cards available for this month.</p>}
      </Card>

      <Card>
        <div className="section-title"><div><h3>Commitments</h3><p className="muted">Plans due, paid, unpaid and overdue for selected month.</p></div><button className="btn small" onClick={() => onNavigate('upcoming')}>Open Plans</button></div>
        <div className="metric-strip">
          <Metric label="Due plans" value={`${model.duePlans.length} / ${formatMoney(model.duePlansTotal)}`} />
          <Metric label="Paid plans" value={`${model.paidPlans.length} / ${formatMoney(model.paidPlansTotal)}`} tone="credit" />
          <Metric label="Unpaid plans" value={`${model.unpaidPlans.length}`} tone={model.unpaidPlans.length ? 'orange' : 'credit'} />
          <Metric label="Overdue plans" value={`${model.overduePlans.length} / ${formatMoney(model.overduePlansTotal)}`} tone={model.overduePlans.length ? 'debit' : 'credit'} />
          <Metric label="Paused plans" value={`${model.pausedPlans.length}`} />
        </div>
      </Card>

      <Card>
        <div className="section-title"><div><h3>Data quality</h3><p className="muted">Critical blockers, warnings and informational checks.</p></div></div>
        <div className="close-issues">{model.issues.map((issue) => <div className={`close-issue ${issue.level}`} key={issue.id}><div><b>{issue.issue}</b><span>{issue.count} item(s){issue.amount ? ` / ${formatMoney(issue.amount)}` : ''}</span></div><small>{issue.action}</small></div>)}</div>
        {!model.issues.length && <p className="compact-empty">No data-quality issues detected for this review.</p>}
      </Card>

      <Card>
        <div className="section-title"><div><h3>Budget and CR/DR review</h3><p className="muted">Warnings here help review readiness but do not block reconciliation by themselves.</p></div></div>
        <div className="metric-strip">
          <Metric label="Budget total" value={formatMoney(model.budgetTotal)} />
          <Metric label="Budget spent" value={formatMoney(model.budgetSpent)} tone={model.budgetExceeded ? 'debit' : 'credit'} />
          <Metric label="Budget remaining" value={formatMoney(model.budgetRemaining)} />
          <Metric label="Exceeded / warning" value={`${model.budgetExceeded} / ${model.budgetWarning}`} />
          <Metric label="You will receive" value={formatMoney(model.crReceivable)} tone="credit" />
          <Metric label="You need to pay" value={formatMoney(model.drPayable)} tone="debit" />
          <Metric label="Settled CR/DR" value={formatMoney(model.crdrSettled)} />
          <Metric label="Open CR/DR" value={`${model.crdrOpenItems}`} />
        </div>
      </Card>

      <Card>
        <div className="section-title"><div><h3>Reconciliation history</h3><p className="muted">Correction means saving a new audit snapshot; old history is preserved.</p></div></div>
        {[...model.accountRows, ...model.cardRows].flatMap((row) => row.history.map((item) => ({ account: row.account, audit: item }))).slice(0, 12).map(({ account, audit: item }) => <AuditHistoryRow key={item.id} account={account} audit={item} />)}
        {!model.accountRows.some((row) => row.history.length) && !model.cardRows.some((row) => row.history.length) && <p className="compact-empty">No reconciliation snapshots saved for this month.</p>}
      </Card>

      <Card>
        <div className="section-title"><div><h3>Month Lock Control</h3><p className="muted">Persist review state, close clean months, and reopen with a reason when corrections are needed.</p></div></div>
        <div className="close-action-panel">
          <div>
            <span className={`close-status ${statusClass(period.status)}`}>{period.status}</span>
            <p className="muted">{period.status === 'CLOSED' ? `Closed ${period.closedAtISO ? displayDate(period.closedAtISO.slice(0, 10)) : 'for edits'}` : period.status === 'REOPENED' ? `Reopened: ${period.reopenReason || 'correction window active'}` : 'Open for review and financial edits.'}</p>
          </div>
          <div className="confirm-actions">
            {period.status !== 'CLOSED' && <button className="btn" disabled={saving} onClick={() => setPeriodAction('review')}>Mark Review Complete</button>}
            {period.status !== 'CLOSED' && <button className="btn primary" disabled={saving || model.criticalIssues.length > 0} onClick={() => setPeriodAction('close')}>Close Month</button>}
            {period.status === 'CLOSED' && <button className="btn danger" disabled={saving} onClick={() => setPeriodAction('reopen')}>Reopen Month</button>}
          </div>
        </div>
        {model.criticalIssues.length > 0 && <p className="muted">Close is disabled until critical blockers are resolved.</p>}
        <div className="close-issues">
          {periodEvents.slice(0, 6).map((event) => <div className="close-issue info" key={event.id}><div><b>{event.eventType}</b><span>{event.createdAtISO ? displayDate(event.createdAtISO.slice(0, 10)) : selectedMonth}</span></div><small>{event.reason || `${event.summary?.criticalCountAtClose ?? 0} critical / ${event.summary?.warningCountAtClose ?? 0} warnings`}</small></div>)}
          {!periodEvents.length && <p className="compact-empty">No lock history for this month yet.</p>}
        </div>
      </Card>
    </>}

    <Modal open={!!audit} title="Save reconciliation snapshot" onClose={() => saving ? undefined : setAudit(null)}>
      {audit && <div className="edit-transaction-form">
        <p className="muted">This saves an audit trail only. System balance remains unchanged.</p>
        <div className="confirm-details"><p><b>Account</b><span>{auditAccount?.name || audit.accountId}</span></p><p><b>System balance</b><span>{formatMoney(audit.systemBalance)}</span></p><p><b>Difference</b><span>{formatMoney(auditDifference)}</span></p>{auditAccount?.type === 'credit_card' && <p><b>Available difference</b><span>{formatMoney(availableDifference)}</span></p>}</div>
        <Field label="Reconciliation date"><input type="date" value={audit.date} onChange={(event) => setAudit({ ...audit, date: event.target.value })} /></Field>
        <Field label={auditAccount?.type === 'credit_card' ? 'Actual outstanding' : 'Actual balance'}><input type="number" inputMode="decimal" value={audit.actualBalance || ''} onChange={(event) => setAudit({ ...audit, actualBalance: Number(event.target.value) })} /></Field>
        {auditAccount?.type === 'credit_card' && <Field label="Actual available limit"><input type="number" inputMode="decimal" value={audit.actualAvailableLimit || ''} onChange={(event) => setAudit({ ...audit, actualAvailableLimit: Number(event.target.value) })} /></Field>}
        <Field label="Note"><input value={audit.note} onChange={(event) => setAudit({ ...audit, note: event.target.value })} /></Field>
        <div className="confirm-actions"><button className="btn" disabled={saving} onClick={() => setAudit(null)}>Cancel</button><button className="btn primary" disabled={saving} onClick={saveAudit}>{saving ? 'Saving...' : 'Save Snapshot'}</button></div>
      </div>}
    </Modal>
    <ConfirmModal
      open={periodAction === 'review'}
      title="Mark review complete?"
      message="This records the monthly review state but keeps the month editable until you close it."
      details={[{ label: 'Month', value: selectedMonth }, { label: 'Critical', value: model.criticalIssues.length }, { label: 'Warnings', value: model.warnings.length }]}
      confirmLabel="Mark Reviewed"
      loading={saving}
      onCancel={() => setPeriodAction(null)}
      onConfirm={runPeriodAction}
    />
    <ConfirmModal
      open={periodAction === 'close'}
      title="Close this month?"
      message="After closing, ordinary financial changes dated in this month will be blocked until the period is reopened."
      details={[{ label: 'Month', value: selectedMonth }, { label: 'Critical blockers', value: model.criticalIssues.length }, { label: 'Warnings', value: model.warnings.length }]}
      confirmLabel="Close Month"
      loading={saving}
      onCancel={() => setPeriodAction(null)}
      onConfirm={runPeriodAction}
    />
    <Modal open={periodAction === 'reopen'} title="Reopen closed month" onClose={() => saving ? undefined : setPeriodAction(null)}>
      <div className="edit-transaction-form">
        <p className="muted">Reopening allows corrections dated in {selectedMonth}. The reason is saved in lock history.</p>
        <Field label="Reason"><input value={reopenReason} onChange={(event) => setReopenReason(event.target.value)} placeholder="Example: missed bank transaction" /></Field>
        <div className="confirm-actions"><button className="btn" disabled={saving} onClick={() => setPeriodAction(null)}>Cancel</button><button className="btn danger" disabled={saving || reopenReason.trim().length < 3} onClick={runPeriodAction}>{saving ? 'Working...' : 'Reopen Month'}</button></div>
      </div>
    </Modal>
  </div>;
}

function AuditHistoryRow({ account, audit }: { account: Account; audit: BalanceAudit }) {
  const mismatch = Math.abs(Number(audit.difference || 0)) > 0 || Math.abs(Number(audit.availableLimitDifference || 0)) > 0;
  return <div className="history-row">
    <div><b>{account.name}</b><span>{displayDate(audit.dateISO || audit.auditDate)} / {mismatch ? 'Mismatch' : 'Matched'} / {audit.note || 'No note'}</span></div>
    <strong>{formatMoney(audit.difference || 0)}</strong>
  </div>;
}
